import DynamicFormInput from "components/form/form";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState, useCallback } from "react";
import { conductLegislativeDrafting } from "types";
import { LoadingIcon } from "utils/constant";
import personImg from "../../images/person.png";
import SingleRegisterDirective from "./fragments/singleRegisterDirective";
import SingleRegisterDirectiveDetail from "./fragments/singleRegisterDirectiveDetail";
import { useTranslation } from "react-i18next";
import FileViewer from "components/file/fileViewContainer";
import Pagination from "components/Pagination";

// Use direct fetch without auth for public access
const fetchPublicDirectives = async () => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://research-api.oagb.et/api/";
  const response = await fetch(
    `${baseUrl}directive_registration/mapped-summary/`,
    {
      method: "GET",
    }
  );
  if (!response.ok) {
    throw new Error("Failed to fetch public directives");
  }
  const data = await response.json();

  // Transform mapped-summary response to match existing structure
  return data.map((item: any, index: number) => {
    const requestId = item["Attached Documents"]?.[0]?.request || `directive-${index}`;
    return {
      uu_id: requestId,
      id: requestId,
      registry_number: item["Reference Number"]?.registry_number || "",
      application_number: item["Reference Number"]?.application_number || "",
      request_letter_reference_number: item["Reference Number"]?.request_letter_reference_number || "",
      organization_name: item["Organization"] || "",
      explanatory_notes: item["Explanatory Notes"] || "",
      summary_of_comments: item["Summary of Comments"] || "",
      written_comments_last_updated: item["Written Comments Last Updated"] || null,
      request_created_date: item["Request Created Date"] || null,
      documents: (item["Attached Documents"] || []).map((doc: any) => ({
        uu_id: doc.uu_id,
        uuid: doc.document_uuid,
        document_type: doc.document_type,
        document_path: doc.document_path,
        ...doc
      })),
      requested_by_user: {
        full_name: item["official name"] || ""
      },
      is_published: item["Is Published"] || false
    }
  });
};

export default function PublicRegisterDirectives() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeDirective, setDirective] = useState<conductLegislativeDrafting>();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useToast();
  const [data, setData] = useState<{
    isLoading: boolean;
    data: conductLegislativeDrafting[];
  }>({ isLoading: true, data: [] });

  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 2;

  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
  };

  // Filter data based on search term
  const filteredData = useCallback(() => {
   if (!searchTerm.trim()) {
     return data.data || [];
   }

     const searchLower = searchTerm.toLowerCase().trim();
     return (data.data || []).filter((req: conductLegislativeDrafting) => {
       return (
         (req.request_letter_reference_number || "").toLowerCase().includes(searchLower) ||
         (req.registry_number || "").toLowerCase().includes(searchLower) ||
         (req.organization_name || "").toLowerCase().includes(searchLower)
       );
     });
   }, [data.data, searchTerm]);

  // Paginate filtered data
  const paginatedData = useCallback(() => {
    const filtered = filteredData();
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filtered.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData().length / pageSize);

  // Update active directive when paginated data changes
  useEffect(() => {
    const currentPaginated = paginatedData();
    if (currentPaginated.length > 0) {
      const currentActiveInPaginated = currentPaginated.find(
        item => item.id === activeDirective?.id
      );

      if (currentActiveInPaginated) {
        setDirective(currentActiveInPaginated);
      } else {
        setDirective(currentPaginated[0]);
      }
    } else {
      setDirective(undefined);
    }
  }, [paginatedData, activeDirective?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSearch = async () => {
    setSubmitting(true);
    try {
      // Frontend only search
    } catch (error) {
      console.error("Search error:", error);
      showToast(t("research.search_failed"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Enter key for search
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setData({ isLoading: true, data: [] });

        const directives = await fetchPublicDirectives();

        // ONLY SHOW PUBLISHED ITEMS
        const publishedDirectives = directives.filter(
          (req: any) => req.is_published === true
        );

        setData({ isLoading: false, data: publishedDirectives });

        if (publishedDirectives.length > 0) {
          setDirective(publishedDirectives[0]);
        }
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching public register directives:", error);
        showToast(t("directives.failed_to_fetch_directive_requests"), "error");
        setData((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [showToast]);

  // Calculate filtered and paginated results
  const currentFilteredResults = filteredData();
  const currentPaginatedResults = paginatedData();
  const hasFilteredResults = currentFilteredResults.length > 0;

  return (
    <div className="flex flex-col md:flex-row h-full gap-4">
      <div className="flex flex-col flex-1 overflow-y-hidden">
        <div className="flex flex-col bg-white rounded-lg shadow-md p-4 md:p-6 mb-4">
          <p className="mb-2 font-medium">{t("directives.search_public_register_directives")}</p>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full">
              <div onKeyDown={handleKeyDown}>
                <DynamicFormInput
                  inputs={[
                    {
                      value: searchTerm,
                      type: "text",
                      label: t("directives.search_by_ref_num_or_org"),
                      name: "search",
                      onChange: handleChange,
                      placeholder: t("Eg12345"),
                    },
                  ]}
                />
              </div>
            </div>
            <button
              onClick={handleSearch}
              disabled={submitting}
              className="bg-[#0A1D39] hover:bg-[#13294d] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-12 rounded-md h-fit w-full md:w-fit"
            >
              {submitting ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon} {t("research.searching")}
                </div>
              ) : (
                t("research.search_button")
              )}
            </button>
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              {t("research.results_count", { count: currentFilteredResults.length, term: searchTerm })}
            </div>
          )}
        </div>

        {data.isLoading ? (
          <div className="text-center mb-auto text-gray-500 py-8">
            <div className="flex justify-center mb-2">
              {LoadingIcon}
            </div>
            <p>{t("directives.loading_public_directives")}</p>
          </div>
        ) : !hasFilteredResults ? (
          <div className="text-center mb-auto text-gray-500 py-8">
            <p>
              {searchTerm.trim()
                ? t("research.no_results_found", { term: searchTerm })
                : t("directives.no_public_register_directives_yet")}
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 overflow-y-scroll scrollbar-hide flex-1">
              {currentPaginatedResults.map((directive: conductLegislativeDrafting) => (
                <SingleRegisterDirective
                  key={directive.id}
                  personImg={personImg}
                  directive={directive}
                  isActive={directive.id === activeDirective?.id}
                  onClick={() => setDirective(directive)}
                />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>

      {/* Detail Panel */}
      <div className="flex flex-col flex-1 h-full overflow-scroll scrollbar-hide">
        {hasFilteredResults && activeDirective ? (
                <SingleRegisterDirectiveDetail
                    directive={activeDirective}
                    fileRowProps={fileRowProps}
                    onUpdate={(updatedDirective: any) => {
                        setDirective(updatedDirective);
                        setData((prev) => ({
                            ...prev,
                            data: prev.data.map((r: conductLegislativeDrafting) =>
                                r.id === updatedDirective.id ? updatedDirective : r
                            ),
                        }));
                    }}
                />
        ) : hasFilteredResults && !activeDirective ? (
          <div className="text-center p-8 text-gray-500">
            <p>Select a directive to view details</p>
          </div>
        ) : null}
      </div>
      <FileViewer
        selectedApplicantDocument={selectedDocument || undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
    </div>
  );
}
